<template lang="pug">
yz-filter-menu(:label="$t('panel.amount')" :is-active="isActive" :display-value="displayValue" :disable-apply="!isValid" @clear="onClear" @apply="onApply")
  .pa-4
    yz-dropdown.mb-3(:items="comparisonOptions" item-text="label" item-value="value" v-model="selectedComparison" :label="$t('panel.comparison')" size="lg")

    template(v-if="selectedComparison === 'between'")
      .d-flex.align-center.gap-2
        yz-icon(size=16) $yzi-corner-down-right
        yz-text-field(v-model.number="minValue" :label="$t('panel.minimum_amount')" type="number" :prefix="$currency.symbol" size="lg" min="0" :rules="amountRules")
        div {{ $t('to') }}
        yz-text-field(v-model.number="maxValue" :label="$t('panel.maximum_amount')" type="number" :prefix="$currency.symbol" size="lg" min="0" :rules="amountRules")
    template(v-else)
      .d-flex.align-center.gap-2
        yz-icon(size=16) $yzi-corner-down-right
        yz-text-field(v-model.number="singleValue" type="number" :prefix="$currency.symbol" size="lg" min="0" :rules="amountRules" width="100%")
</template>

<script>
import { validateDecimalPlaces } from '@/utils'

export default {
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedComparison: 'exact',
      singleValue: null,
      minValue: null,
      maxValue: null,
      isActive: false,
      displayValue: ''
    }
  },
  computed: {
    comparisonOptions() {
      return [
        { label: this.$t('panel.is'), value: 'exact' },
        { label: this.$t('panel.is_not'), value: 'ne' },
        { label: this.$t('panel.less_than'), value: 'lt' },
        { label: this.$t('panel.more_than'), value: 'gt' },
        { label: this.$t('panel.is_between'), value: 'between' }
      ]
    },
    amountRules() {
      return [
        v => v === null || v === '' || v >= 0 || this.$t('amount_cannot_be_negative'),
        v => validateDecimalPlaces(v, this.$currency)
      ]
    },
    isValid() {
      if (this.selectedComparison === 'between') {
        const minValid = this.minValue !== null && this.minValue >= 0 &&
                        this.amountRules.every(rule => rule(this.minValue) === true)
        const maxValid = this.maxValue !== null && this.maxValue >= 0 &&
                        this.amountRules.every(rule => rule(this.maxValue) === true)
        return minValid && maxValid && this.minValue <= this.maxValue
      }
      return this.singleValue !== null && this.singleValue >= 0 &&
             this.amountRules.every(rule => rule(this.singleValue) === true)
    }
  },
  methods: {
    onClear() {
      this.singleValue = null
      this.minValue = null
      this.maxValue = null
      this.selectedComparison = 'exact'
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    onApply() {
      this.isActive = true

      let filterData = {}
      const comparisonLabel = this.comparisonOptions.find(o => o.value === this.selectedComparison)?.label || ''

      if (this.selectedComparison === 'between') {
        // Convert display values to cent values for API
        const minCentValue = Math.round(this.minValue * this.$currency.multiplier)
        const maxCentValue = Math.round(this.maxValue * this.$currency.multiplier)

        // Format display with currency symbol
        this.displayValue = `${comparisonLabel} ${this.$options.filters.displayPrice(minCentValue)} - ${this.$options.filters.displayPrice(maxCentValue)}`
        filterData = {
          key: 'amount__range',
          value: [minCentValue, maxCentValue],
          params: {
            amount__gte: minCentValue,
            amount__lte: maxCentValue
          }
        }
      } else {
        const suffixMap = {
          gt: '__gt',
          lt: '__lt',
          ne: '__ne',
          exact: ''
        }
        const key = `amount${suffixMap[this.selectedComparison]}`

        // Convert display value to cent value for API
        const centValue = Math.round(this.singleValue * this.$currency.multiplier)

        this.displayValue = `${comparisonLabel} ${this.$options.filters.displayPrice(centValue)}`
        filterData = {
          key,
          value: centValue
        }
      }

      this.$emit('update:filter', filterData)
    },
    reset() {
      this.onClear()
    },
    restoreFromParams(params) {
      // Restore filter state from URL params
      // Params can be: { amount: value }, { amount__gt: value }, { amount__gte, amount__lte }
      if (params.amount !== undefined) {
        this.selectedComparison = 'exact'
        this.singleValue = params.amount / this.$currency.multiplier
      } else if (params.amount__gt !== undefined) {
        this.selectedComparison = 'gt'
        this.singleValue = params.amount__gt / this.$currency.multiplier
      } else if (params.amount__lt !== undefined) {
        this.selectedComparison = 'lt'
        this.singleValue = params.amount__lt / this.$currency.multiplier
      } else if (params.amount__ne !== undefined) {
        this.selectedComparison = 'ne'
        this.singleValue = params.amount__ne / this.$currency.multiplier
      } else if (params.amount__gte !== undefined && params.amount__lte !== undefined) {
        this.selectedComparison = 'between'
        this.minValue = params.amount__gte / this.$currency.multiplier
        this.maxValue = params.amount__lte / this.$currency.multiplier
      }

      // Apply to set isActive and displayValue
      if (this.isValid) {
        this.onApply()
      }
    }
  }
}
</script>
